// =============================================================
// i18n — minimal translation engine with HU/EN toggle + strength/yoga mode
// =============================================================
//
// - Reads JSON dictionaries and looks up dotted keys (`nav.about`)
// - Persists choice to localStorage, syncs with URL pathname (/en, /hu)
// - Falls back gracefully on missing keys
// - Exposes `t()`, `applyI18n()`, and a tiny pub/sub for re-render
//
// Mode axis:
//   - Each locale has TWO self-contained dictionaries (strength + yoga)
//     so the merge `{...shared, ...active}` would be a no-op — `activeDict()`
//     just returns the current mode's dict directly.
//   - The active mode is read via `getMode()` from `./mode.ts` at lookup
//     time, so a mid-session mode flip is reflected on the next `t()` call.

import { getMode, type Mode } from './mode';
import huStrength from '../i18n/strength.hu.json';
import huYoga from '../i18n/yoga.hu.json';
import enStrength from '../i18n/strength.en.json';
import enYoga from '../i18n/yoga.en.json';

export type Locale = 'hu' | 'en';
export type Dict = Record<string, unknown>;

const DICTS: Record<Locale, Record<Mode, Dict>> = {
  hu: { strength: huStrength, yoga: huYoga },
  en: { strength: enStrength, yoga: enYoga },
};
export const LOCALES: Locale[] = ['hu', 'en'];
export const DEFAULT_LOCALE: Locale = 'hu';

/** Active dictionary for the current locale + mode. Each mode file is
 *  self-contained (full dict), so no merge is needed — just look up the
 *  active mode's dict. */
function activeDict(): Dict {
  return DICTS[_locale][getMode()];
}

const STORAGE_KEY = 'studio333:locale';
const URL_LOCALE_RE = /^\/(hu|en)(?:\/|$)/;

let _locale: Locale = DEFAULT_LOCALE;
const subscribers = new Set<() => void>();

const isLocale = (s: string): s is Locale => s === 'hu' || s === 'en';

// ---------- Reading the locale (URL > localStorage > default) ----------
// Magyar is the default. A visitor's browser language is intentionally NOT
// consulted — we want the home audience to land on Hungarian even when their
// OS or browser is set to English. They can switch via the lang toggle, which
// then persists to localStorage and wins on subsequent visits.
function detectInitial(): Locale {
  // 1) URL pathname override (/en, /en/anything)
  if (typeof location !== 'undefined') {
    const m = URL_LOCALE_RE.exec(location.pathname);
    if (m && isLocale(m[1])) return m[1];
  }

  // 2) localStorage (a previous explicit choice wins)
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isLocale(stored)) return stored;
  } catch { /* SSR / private mode */ }

  return DEFAULT_LOCALE;
}

// ---------- Subscription ----------
export function onLocaleChange(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

// ---------- Translation ----------
function getPath(dict: Dict, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Dict)) {
      return (acc as Dict)[key];
    }
    return undefined;
  }, dict);
}

/** Translate a dotted key. Optional `vars` object enables `{{name}}` interpolation. */
export function t(key: string, vars?: Record<string, string | number>): string {
  // First try the active mode, then fall back to the strength variant of the
  // active locale (yoga might omit a key the strength variant has), then the
  // default locale. Mirrors the existing 2-tier fallback shape.
  const active = activeDict();
  const strengthFallback = DICTS[_locale].strength;
  const defaultLocale = DICTS[DEFAULT_LOCALE].strength;
  const raw = getPath(active, key)
    ?? getPath(strengthFallback, key)
    ?? getPath(defaultLocale, key)
    ?? key;
  let str = typeof raw === 'string' ? raw : String(raw);
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      str = str.replaceAll(`{{${k}}}`, String(v));
    }
  }
  return str;
}

/** Like `t()` but returns the raw value (use for arrays/objects in the dictionaries). */
export function tRaw<T = unknown>(key: string): T | undefined {
  const active = activeDict();
  const strengthFallback = DICTS[_locale].strength;
  const defaultLocale = DICTS[DEFAULT_LOCALE].strength;
  return (getPath(active, key) as T | undefined)
    ?? (getPath(strengthFallback, key) as T | undefined)
    ?? (getPath(defaultLocale, key) as T | undefined);
}

/** Convenience for array values. */
export function tList(key: string): string[] {
  const v = tRaw<string[]>(key);
  return Array.isArray(v) ? v : [];
}

export function getLocale(): Locale {
  return _locale;
}

// ---------- Switching ----------
export function setLocale(next: Locale, opts: { syncPath?: boolean } = {}): void {
  if (!isLocale(next) || next === _locale) return;
  _locale = next;
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
  if (opts.syncPath && typeof location !== 'undefined') {
    // Strip any existing language prefix from the current path, then re-prefix.
    const raw = location.pathname.replace(/^\//, '');
    const cleaned = raw.replace(/^(?:hu|en)(?=\/|$)/, '').replace(/^\//, '');
    const target = cleaned ? `/${next}/${cleaned}` : `/${next}`;
    if (target !== location.pathname) {
      history.pushState({}, '', target);
    }
  }
  document.documentElement.lang = next;
  // Run each subscriber in its own try/catch: one listener throwing must
  // never prevent the rest from running (we'd otherwise leave the nav, the
  // body copy, or the SEO tags out of sync with the new locale).
  subscribers.forEach(cb => {
    try {
      cb();
    } catch (err) {
      console.error('[i18n] subscriber threw during setLocale:', err);
    }
  });
}

// ---------- Apply data-i18n to the DOM ----------
export function applyI18n(root: ParentNode = document): void {
  // text content
  root.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    el.textContent = t(el.dataset.i18n ?? '');
  });
  // attributes
  root.querySelectorAll<HTMLElement>('[data-i18n-attr]').forEach((el) => {
    const spec = el.dataset.i18nAttr ?? '';
    spec.split(';').forEach((pair) => {
      const [attr, key] = pair.split(':').map(s => s.trim());
      if (attr && key) el.setAttribute(attr, t(key));
    });
  });
}

// ---------- Initialise ----------
export function initI18n(): Locale {
  _locale = detectInitial();
  document.documentElement.lang = _locale;
  if (typeof window !== 'undefined') {
    window.addEventListener('popstate', () => {
      const m = URL_LOCALE_RE.exec(location.pathname);
      if (m && isLocale(m[1]) && m[1] !== _locale) {
        setLocale(m[1]);
      }
    });
  }
  return _locale;
}